import { useEffect } from "react";
import type { PlayerProfile } from "@chessla/shared";
import type { UpdatePlayerFields } from "./profile.api";
import { useProfileStore } from "./profile.store";
import { useAuth } from "@/app/context/AuthContext";
import { publishNotification } from "@/shared/notifications/notification-bus";

export interface UseProfileReturn {
  profile: PlayerProfile | null;
  notOnboarded: boolean;
  savedAt: number | null;
  handleFieldChange: (fields: UpdatePlayerFields) => void;
  refetch: () => Promise<void>;
}

export function useProfile(): UseProfileReturn {
  const { userId } = useAuth();
  const ownerUserId = useProfileStore((state) => state.ownerUserId);
  const storedProfile = useProfileStore((state) => state.profile);
  const storedNotOnboarded = useProfileStore((state) => state.notOnboarded);
  const profile = ownerUserId === userId ? storedProfile : null;
  const notOnboarded = ownerUserId === userId ? storedNotOnboarded : false;
  const savedAt = useProfileStore((state) => state.savedAt);
  const loadProfile = useProfileStore((state) => state.loadProfile);
  const refreshProfile = useProfileStore((state) => state.refreshProfile);
  const optimisticallyUpdateProfile = useProfileStore((state) => state.optimisticallyUpdateProfile);
  const updateProfile = useProfileStore((state) => state.updateProfile);

  useEffect(() => {
    void loadProfile(userId).catch(() => undefined);
  }, [loadProfile, userId]);

  /** Optimistically updates local state, then persists to the server. */
  const handleFieldChange = (fields: UpdatePlayerFields) => {
    optimisticallyUpdateProfile(fields);
    void updateProfile(fields).catch(() => {
      if (useProfileStore.getState().ownerUserId === userId)
        publishNotification("Your Nikola context could not be saved. Please try again.", "error");
    });
  };

  /** Refetch the profile (used after linking) */
  const refetch = async () => {
    await refreshProfile();
  };

  return { profile, notOnboarded, savedAt, handleFieldChange, refetch };
}
